import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { APIURL, TZ } from '../../../../Global';
import { sampleList } from '../sampleList';
import AddItemBase from './AddItemBase';
import { useAlert } from 'react-alert';
import { FaCheck, FaTrash } from 'react-icons/fa';

const AddNew = ({ group, handleSuccess, type, onClose }) => {
  const alert = useAlert();
  const [sample, setSample] = useState('blood');
  const [selectedItems, setSelectedItems] = useState([]);
  const [options, setOptions] = useState([]);
  const [initialItems, setInitialItems] = useState([]);

  useEffect(() => {
    if (group?.panels) {
      const initial = group.panels.map(item => item.id);
      setInitialItems(initial);
      setSelectedItems(
        group.panels.map(item => ({ 
          ...item, 
          added: true 
        }))
      );
    }
  }, [group]);

  useEffect(() => {
    const fetchData = async () => {
      const tokenString = sessionStorage.getItem('usertoken');
      const str = tokenString?.replace(/["]+/g, '');

      try {
        const response = await axios.get(`${APIURL}/api/v1/staff/lab-test/?sample=${sample}&${TZ}`, {
          headers: { Authorization: 'Token ' + str }
        });

        if (response.data.status === 'success') {
          setOptions(response.data[type === 'panel' ? 'panels' : 'tests'].map(item => ({
            ...item,
            id: item.id,
            name: type === 'panel' ? item.panel_name : item.test_name,
            sample,
          })));
        }
      } catch (error) {
        console.error(`Error fetching ${type} data:`, error);
        setOptions([]);
        alert.error(`Error fetching ${type} data.`);
      }
    };

    fetchData();
  }, [sample, type, alert]);

  const handleCategoryClick = (category) => {
    setSample(category);
  };

  const handleSelectItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.some(i => i.id === item.id);
      const isInitial = initialItems.includes(item.id);

      if (exists) {
        // Don't allow deselecting initially added items
        if (isInitial) return prev;
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, { ...item, added: false }];
      }
    });
  };

  const handleRemoveItem = (item) => {
    // Don't allow removing initially added items
    if (item.added) return;
    setSelectedItems(prev => prev.filter(i => i.id !== item.id));
  };

  const handleSubmit = async () => {
    if (!group?.id) {
      alert.error('Group is mandatory');
      return;
    }

    const payload = {
      method: 'add',
      group_id: group.id,
      [type === 'panel' ? 'panels' : 'tests']: selectedItems.map(item => String(item.id))
    };

    try {
      const tokenString = sessionStorage.getItem('usertoken');
      const str = tokenString?.replace(/["]+/g, '');

      const res = await axios.put(
        `${APIURL}/api/v1/staff/lab-test/?${TZ}`,
        payload,
        { headers: { Authorization: 'Token ' + str } }
      );

      if (res.status === 200 && res.data.status === 'success') {
        alert.success(`${type === 'panel' ? 'Panels' : 'Tests'} updated successfully!`);
        handleSuccess();
        onClose();
      } else {
        alert.error(res.data.message || `Failed to update ${type === 'panel' ? 'panels' : 'tests'}.`);
      }
    } catch (err) {
      alert.error(`Failed to update ${type === 'panel' ? 'panels' : 'tests'}. Please try again.`);
      console.error('API Error:', err);
    }
  };

  const filteredOptions = (searchTerm) => {
    return options.filter(item => 
      !searchTerm || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderSamplesColumn = () => (
    <div className="samples-column">
      <div className="samples-header">Samples</div>
      <div className="samples-list" style={{ borderRadius: 0, border: "none" }}>
        {sampleList.map((category) => (
          <button
            key={category}
            className={`sample-button ${sample === category ? "active" : ""}`}
            onClick={() => handleCategoryClick(category)}
          >
            {category}
          </button>
        ))}
      </div>
    </div>
  );

  const renderItemsList = (searchTerm) => {
    const items = filteredOptions(searchTerm);
    return items.length > 0 ? (
      items.map((item) => {
        const isSelected = selectedItems.some(selected => selected.id === item.id);
        const isInitial = initialItems.includes(item.id);
        
        return (
          <div
            key={item.id}
            className={`item-card ${isSelected ? 'selected' : ''} ${isInitial ? 'initial-item' : ''}`}
            onClick={() => !isInitial && handleSelectItem(item)}
            title={isInitial ? "This item was previously added and cannot be removed" : ""}
          >
            <div className="item-content">
              {item.name}
              <span className="item-sample">{item.sample}</span>
              {isInitial && <span className="initial-badge">(Already added)</span>}
            </div>
            {isSelected && <FaCheck className="selected-icon" />}
          </div>
        );
      })
    ) : (
      <div className="empty-message">
        {searchTerm
          ? `No ${type === 'panel' ? 'panels' : 'tests'} match your search`
          : `No ${type === 'panel' ? 'panels' : 'tests'} available`}
      </div>
    );
  };

  const renderSelectedList = () => {
    return selectedItems.length > 0 ? (
      selectedItems.map((item) => (
        <div key={item.id} className={`selected-item ${item.added ? 'initial-item' : ''}`}>
          <span>
            {item.name}
            <span className="item-sample">{item.sample}</span>
            {item.added && <span className="added-badge">(Already added)</span>}
          </span>
          {!item.added && (
            <FaTrash
              className="remove-icon"
              onClick={() => handleRemoveItem(item)}
            />
          )}
        </div>
      ))
    ) : (
      <div className="empty-message">
        No {type === 'panel' ? 'panels' : 'tests'} selected
      </div>
    );
  };

  return (
    <AddItemBase
      type={type}
      group={group}
      onClose={onClose}
      handleSuccess={handleSuccess}
      renderSamplesColumn={renderSamplesColumn}
      renderItemsList={renderItemsList}
      renderSelectedList={renderSelectedList}
      handleSubmit={handleSubmit}
      selectedItems={selectedItems}
    />
  );
};

export default AddNew;